import React, { ChangeEvent, ReactNode } from "react";
import { Property } from "../../types/property";
import { isLetAgreedStatus } from "../../util/propertyParsing";

export interface PropertyFilters {
  propertyType: string;
  availableFrom: string;
  minPrice: number | null;
  maxPrice: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  isFurnished: boolean | null;
  newHome: boolean | null;
  status: string;
  minArea: number | null;
  minDeposit: number | null;
  maxDeposit: number | null;
  keyword: string;
}

export const DEFAULT_FILTERS: PropertyFilters = {
  propertyType: "",
  availableFrom: "",
  minPrice: null,
  maxPrice: null,
  bedrooms: null,
  bathrooms: null,
  isFurnished: null,
  newHome: null,
  status: "available",
  minArea: null,
  minDeposit: null,
  maxDeposit: null,
  keyword: "",
};

interface FilterProps {
  value: PropertyFilters;
  onApply: (next: PropertyFilters) => void;
  className?: string;
  variant?: "full" | "basic";
  compact?: boolean;
  actionsInline?: ReactNode;
  basicGridClassName?: string;
  fullGridClassName?: string;
  actionsBarMode?: "default" | "bottom";
}

export interface CollapsibleFilterProps {
  value: PropertyFilters;
  onApply: (next: PropertyFilters) => void;
  showAdvanced: boolean;
  onToggle: (next: boolean) => void;
  className?: string;
  compact?: boolean;
  basicGridClassName?: string;
  fullGridClassName?: string;
}

function getFilterStyles(compact: boolean) {
  const field = compact
    ? "w-full px-2 py-1 text-sm text-text-obsidian border border-solid border-[rgba(0,0,0,0.12)] bg-white font-manrope transition-colors focus:border-brand-yellow focus:outline-none"
    : "w-full px-3 py-1 text-text-obsidian border border-solid border-[#ccc] bg-white font-manrope clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] transition-all duration-300 focus:border-brand-yellow focus:outline-none";

  return {
    label: compact
      ? "font-manrope text-[10px] uppercase tracking-wider text-text-slate block mb-0.5"
      : "font-teko text-lg text-text-grey block mb-0.5",
    field,
    btn: compact
      ? "px-4 py-1 font-teko text-sm uppercase transition-all duration-300"
      : "px-6 py-1 font-teko text-lg uppercase transition-all duration-300",
    btnReset:
      "bg-white text-text-obsidian border border-solid border-[#ccc] hover:bg-[#f5f5f5]",
    btnApply:
      "bg-text-obsidian text-white border border-solid border-text-obsidian hover:bg-brand-yellow hover:text-black",
    gridGap: compact ? "gap-2" : "gap-4",
    actionsMt: compact ? "mt-2" : "mt-4",
  };
}

const parseNumber = (value: string): number | null =>
  value === "" ? null : Number(value);

const parseBoolean = (value: string): boolean | null => {
  if (value === "") return null;
  return value === "true";
};

export const applyPropertyFilters = (
  properties: Property[],
  filters: PropertyFilters
) => {
  return properties.filter((property) => {
    if (
      filters.propertyType &&
      property.property_type !== filters.propertyType
    ) {
      return false;
    }

    if (filters.availableFrom) {
      const propDate = property.date_available
        ? new Date(property.date_available)
        : null;
      const filterDate = new Date(filters.availableFrom);
      if (!propDate || propDate < filterDate) return false;
    }

    if (
      typeof filters.minPrice === "number" &&
      property.price < filters.minPrice
    ) {
      return false;
    }

    if (
      typeof filters.maxPrice === "number" &&
      property.price > filters.maxPrice
    ) {
      return false;
    }

    if (
      typeof filters.bedrooms === "number" &&
      (property.bedrooms ?? 0) < filters.bedrooms
    ) {
      return false;
    }

    if (
      typeof filters.bathrooms === "number" &&
      (property.bathrooms ?? 0) < filters.bathrooms
    ) {
      return false;
    }

    if (
      filters.isFurnished !== null &&
      Boolean(property.is_furnished) !== filters.isFurnished
    ) {
      return false;
    }

    if (
      filters.newHome !== null &&
      Boolean(property.new_home) !== filters.newHome
    ) {
      return false;
    }

    if (filters.status === "available" && isLetAgreedStatus(property.status)) {
      return false;
    } else if (
      filters.status &&
      filters.status !== "available" &&
      filters.status !== "all" &&
      (property.status ?? "").toLowerCase() !== filters.status.toLowerCase()
    ) {
      return false;
    }

    if (
      typeof filters.minArea === "number" &&
      (property.internal_area ?? 0) < filters.minArea
    ) {
      return false;
    }

    if (
      typeof filters.minDeposit === "number" &&
      (property.deposit ?? 0) < filters.minDeposit
    ) {
      return false;
    }

    if (
      typeof filters.maxDeposit === "number" &&
      (property.deposit ?? Infinity) > filters.maxDeposit
    ) {
      return false;
    }

    if (filters.keyword.trim()) {
      const needle = filters.keyword.trim().toLowerCase();
      const haystack = `${property.display_address} ${property.postcode ?? ""}`.toLowerCase();
      if (!haystack.includes(needle)) {
        return false;
      }
    }

    return true;
  });
};

const priceOptions = [
  null,
  1000,
  1500,
  2000,
  2500,
  3000,
  4000,
  5000,
  7500,
  10000,
];
const bedroomOptions = [null, 1, 2, 3, 4, 5];
const bathroomOptions = [null, 1, 2, 3, 4];
const areaOptions = [null, 500, 750, 1000, 1500, 2000];
const depositOptions = [null, 1000, 2000, 3000, 5000, 7500, 10000];

const Filter: React.FC<FilterProps> = ({
  value,
  onApply,
  className,
  variant = "full",
  compact = false,
  actionsInline,
  basicGridClassName,
  fullGridClassName,
  actionsBarMode = "default",
}) => {
  const [draft, setDraft] = React.useState<PropertyFilters>(value);
  const ui = getFilterStyles(compact);

  React.useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleInput =
    (key: keyof PropertyFilters) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      let value: PropertyFilters[keyof PropertyFilters] = e.target.value;
      if (
        key === "minPrice" ||
        key === "maxPrice" ||
        key === "bedrooms" ||
        key === "bathrooms" ||
        key === "minArea" ||
        key === "minDeposit" ||
        key === "maxDeposit"
      ) {
        value = parseNumber(e.target.value) as any;
      }
      if (key === "isFurnished" || key === "newHome") {
        value = parseBoolean(e.target.value) as any;
      }
      setDraft((prev) => ({ ...prev, [key]: value }));
    };

  const handleReset = () => {
    setDraft(DEFAULT_FILTERS);
    onApply(DEFAULT_FILTERS);
  };

  const renderBasicActions = () => (
    <div className="flex items-center gap-2 shrink-0">
      <button
        type="button"
        onClick={handleReset}
        className={`${ui.btn} ${ui.btnReset}`}
      >
        Reset
      </button>
      <button
        type="button"
        onClick={() => onApply(draft)}
        className={`${ui.btn} ${ui.btnApply}`}
      >
        Apply
      </button>
      {actionsInline}
    </div>
  );

  const renderBasic = () => (
    <div className={className}>
      {compact ? (
        <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:gap-3">
          <div
            className={
              basicGridClassName ??
              `grid flex-1 w-full ${ui.gridGap} grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 items-end`
            }
          >
            <div>
              <span className={ui.label}>Min Budget (PCM)</span>
              <select
                title="Min Budget (PCM)"
                value={draft.minPrice ?? ""}
                onChange={handleInput("minPrice")}
                className={ui.field}
              >
                {priceOptions.map((p) => (
                  <option key={`min-${p ?? "any"}`} value={p ?? ""}>
                    {p ? `£${p.toLocaleString()}+` : "Any"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className={ui.label}>Max Budget (PCM)</span>
              <select
                title="Max Budget (PCM)"
                value={draft.maxPrice ?? ""}
                onChange={handleInput("maxPrice")}
                className={ui.field}
              >
                {priceOptions.map((p) => (
                  <option key={`max-${p ?? "any"}`} value={p ?? ""}>
                    {p ? `Up to £${p.toLocaleString()}` : "Any"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className={ui.label}>Search</span>
              <input
                title="Search address or postcode"
                type="text"
                value={draft.keyword}
                onChange={handleInput("keyword")}
                placeholder="Address or postcode"
                className={ui.field}
              />
            </div>
            <div>
              <span className={ui.label}>Bedrooms (min)</span>
              <select
                title="Bedrooms (min)"
                value={draft.bedrooms ?? ""}
                onChange={handleInput("bedrooms")}
                className={ui.field}
              >
                {bedroomOptions.map((b) => (
                  <option key={`bed-${b ?? "any"}`} value={b ?? ""}>
                    {b ? `${b}+` : "Any"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className={ui.label}>Bathrooms (min)</span>
              <select
                title="Bathrooms (min)"
                value={draft.bathrooms ?? ""}
                onChange={handleInput("bathrooms")}
                className={ui.field}
              >
                {bathroomOptions.map((b) => (
                  <option key={`bath-${b ?? "any"}`} value={b ?? ""}>
                    {b ? `${b}+` : "Any"}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {renderBasicActions()}
        </div>
      ) : (
        <>
          <div
            className={
              basicGridClassName ??
              `grid w-full ${ui.gridGap} sm:grid-cols-2 lg:grid-cols-4 items-end`
            }
          >
            <div>
              <span className={ui.label}>Min Budget (PCM)</span>
              <select
                title="Min Budget (PCM)"
                value={draft.minPrice ?? ""}
                onChange={handleInput("minPrice")}
                className={ui.field}
              >
                {priceOptions.map((p) => (
                  <option key={`min-${p ?? "any"}`} value={p ?? ""}>
                    {p ? `£${p.toLocaleString()}+` : "Any"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className={ui.label}>Max Budget (PCM)</span>
              <select
                title="Max Budget (PCM)"
                value={draft.maxPrice ?? ""}
                onChange={handleInput("maxPrice")}
                className={ui.field}
              >
                {priceOptions.map((p) => (
                  <option key={`max-${p ?? "any"}`} value={p ?? ""}>
                    {p ? `Up to £${p.toLocaleString()}` : "Any"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className={ui.label}>Search</span>
              <input
                title="Search address or postcode"
                type="text"
                value={draft.keyword}
                onChange={handleInput("keyword")}
                placeholder="Address or postcode"
                className={ui.field}
              />
            </div>
            <div>
              <span className={ui.label}>Bedrooms (min)</span>
              <select
                title="Bedrooms (min)"
                value={draft.bedrooms ?? ""}
                onChange={handleInput("bedrooms")}
                className={ui.field}
              >
                {bedroomOptions.map((b) => (
                  <option key={`bed-${b ?? "any"}`} value={b ?? ""}>
                    {b ? `${b}+` : "Any"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className={ui.label}>Bathrooms (min)</span>
              <select
                title="Bathrooms (min)"
                value={draft.bathrooms ?? ""}
                onChange={handleInput("bathrooms")}
                className={ui.field}
              >
                {bathroomOptions.map((b) => (
                  <option key={`bath-${b ?? "any"}`} value={b ?? ""}>
                    {b ? `${b}+` : "Any"}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div
            className={`flex items-end gap-2 justify-between w-full ${ui.actionsMt}`}
          >
            {renderBasicActions()}
          </div>
        </>
      )}
    </div>
  );

  const renderFull = () => (
    <div
      className={`${className ?? ""} ${
        actionsBarMode === "bottom" ? "relative h-full flex flex-col" : ""
      }`}
    >
      <div
        className={
          actionsBarMode === "bottom" ? "flex-1 min-h-0 overflow-y-auto" : ""
        }
      >
        <div
          className={
            fullGridClassName ??
            (compact
              ? `grid grid-cols-2 ${ui.gridGap} md:grid-cols-3 lg:grid-cols-4`
              : "grid grid-cols-2 gap-4 lg:grid-cols-3 md:grid-cols-2")
          }
        >
          <div>
            <span className={ui.label}>
              Search
            </span>
            <input
              title="Search address or postcode"
              type="text"
              value={draft.keyword}
              onChange={handleInput("keyword")}
              placeholder="Address or postcode"
              className={ui.field}
            />
          </div>

          <div>
            <span className={ui.label}>
              Status
            </span>
            <select
              title="Status"
              value={draft.status}
              onChange={handleInput("status")}
              className={ui.field}
            >
              <option value="available">Available (hide Let Agreed)</option>
              <option value="all">All statuses</option>
              <option value="Available">Available only</option>
              <option value="Let Agreed">Let Agreed only</option>
            </select>
          </div>

          <div>
            <span className={ui.label}>
              Property Type
            </span>
            <select
              title="Property Type"
              value={draft.propertyType}
              onChange={handleInput("propertyType")}
              className={ui.field}
            >
              <option value="">Any</option>
              <option value="Apartment">Apartment</option>
              <option value="Flat">Flat</option>
              <option value="House">House</option>
              <option value="Studio">Studio</option>
            </select>
          </div>

          <div>
            <span className={ui.label}>
              Available From
            </span>
            <input
              title="Available From"
              type="date"
              value={draft.availableFrom}
              onChange={handleInput("availableFrom")}
              className={ui.field}
            />
          </div>

          <div>
            <span className={ui.label}>
              Min Budget (PCM)
            </span>
            <select
              title="Min Budget (PCM)"
              value={draft.minPrice ?? ""}
              onChange={handleInput("minPrice")}
              className={ui.field}
            >
              {priceOptions.map((p) => (
                <option key={`min-${p ?? "any"}`} value={p ?? ""}>
                  {p ? `£${p.toLocaleString()}+` : "Any"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className={ui.label}>
              Max Budget (PCM)
            </span>
            <select
              title="Max Budget (PCM)"
              value={draft.maxPrice ?? ""}
              onChange={handleInput("maxPrice")}
              className={ui.field}
            >
              {priceOptions.map((p) => (
                <option key={`max-${p ?? "any"}`} value={p ?? ""}>
                  {p ? `Up to £${p.toLocaleString()}` : "Any"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className={ui.label}>
              Bedrooms (min)
            </span>
            <select
              title="Bedrooms (min)"
              value={draft.bedrooms ?? ""}
              onChange={handleInput("bedrooms")}
              className={ui.field}
            >
              {bedroomOptions.map((b) => (
                <option key={`bed-${b ?? "any"}`} value={b ?? ""}>
                  {b ? `${b}+` : "Any"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className={ui.label}>
              Bathrooms (min)
            </span>
            <select
              title="Bathrooms (min)"
              value={draft.bathrooms ?? ""}
              onChange={handleInput("bathrooms")}
              className={ui.field}
            >
              {bathroomOptions.map((b) => (
                <option key={`bath-${b ?? "any"}`} value={b ?? ""}>
                  {b ? `${b}+` : "Any"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className={ui.label}>
              Furnished
            </span>
            <div className="flex gap-2">
              {[
                { label: "Any", value: null },
                { label: "Yes", value: true },
                { label: "No", value: false },
              ].map((opt) => (
                <button
                  type="button"
                  key={`furn-${opt.label}`}
                  onClick={() =>
                    setDraft((prev) => ({ ...prev, isFurnished: opt.value }))
                  }
                  className={`px-3 py-1 border border-solid border-[#ccc] font-manrope text-sm transition-all duration-200 ${
                    draft.isFurnished === opt.value
                      ? "bg-brand-yellow text-text-obsidian border-brand-yellow"
                      : "bg-white hover:border-text-dark"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className={ui.label}>
              New Home
            </span>
            <div className="flex gap-2">
              {[
                { label: "Any", value: null },
                { label: "Yes", value: true },
                { label: "No", value: false },
              ].map((opt) => (
                <button
                  type="button"
                  key={`new-${opt.label}`}
                  onClick={() =>
                    setDraft((prev) => ({ ...prev, newHome: opt.value }))
                  }
                  className={`px-3 py-1 border border-solid border-[#ccc] font-manrope text-sm transition-all duration-200 ${
                    draft.newHome === opt.value
                      ? "bg-brand-yellow text-text-obsidian border-brand-yellow"
                      : "bg-white hover:border-text-dark"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className={ui.label}>
              Min Area (sq ft)
            </span>
            <select
              title="Min Area (sq ft)"
              value={draft.minArea ?? ""}
              onChange={handleInput("minArea")}
              className={ui.field}
            >
              {areaOptions.map((a) => (
                <option key={`area-${a ?? "any"}`} value={a ?? ""}>
                  {a ? `${a.toLocaleString()}+ sq ft` : "Any"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className={ui.label}>
              Min Deposit
            </span>
            <select
              title="Min Deposit"
              value={draft.minDeposit ?? ""}
              onChange={handleInput("minDeposit")}
              className={ui.field}
            >
              {depositOptions.map((d) => (
                <option key={`min-dep-${d ?? "any"}`} value={d ?? ""}>
                  {d ? `£${d.toLocaleString()}+` : "Any"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className={ui.label}>
              Max Deposit
            </span>
            <select
              title="Max Deposit"
              value={draft.maxDeposit ?? ""}
              onChange={handleInput("maxDeposit")}
              className={ui.field}
            >
              {depositOptions.map((d) => (
                <option key={`max-dep-${d ?? "any"}`} value={d ?? ""}>
                  {d ? `Up to £${d.toLocaleString()}` : "Any"}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div
        className={
          actionsBarMode === "bottom"
            ? "shrink-0 border-t border-solid border-[#ddd] bg-white p-3"
            : `${ui.actionsMt} flex items-center justify-between gap-3`
        }
      >
        {actionsBarMode === "bottom" ? (
          <div className="grid grid-cols-2 gap-2 w-full">
            <button
              type="button"
              onClick={handleReset}
              className={`w-full ${ui.btn} ${ui.btnReset}`}
            >
              <span className="block">Reset</span>
            </button>
            <button
              type="button"
              onClick={() => onApply(draft)}
              className={`w-full ${ui.btn} ${ui.btnApply}`}
            >
              <span className="block">Apply</span>
            </button>
          </div>
        ) : (
          <>
            {actionsInline && <div className="flex-1">{actionsInline}</div>}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleReset}
                className={`${ui.btn} ${ui.btnReset} mr-2`}
              >
                <span className="block">Reset</span>
              </button>
              <button
                type="button"
                onClick={() => onApply(draft)}
                className={`${ui.btn} ${ui.btnApply}`}
              >
                <span className="block">Apply</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return variant === "basic" ? renderBasic() : renderFull();
};

export const FilterWithCollapse: React.FC<CollapsibleFilterProps> = ({
  value,
  onApply,
  showAdvanced,
  onToggle,
  className,
  compact = false,
  basicGridClassName,
  fullGridClassName,
}) => {
  const moreBtnClass = compact
    ? "font-manrope text-xs text-text-slate underline hover:text-text-obsidian whitespace-nowrap"
    : "underline text-text-grey hover:border-text-dark";

  return showAdvanced ? (
    <div className={className}>
      <Filter
        value={value}
        onApply={onApply}
        variant="full"
        compact={compact}
        fullGridClassName={fullGridClassName}
        actionsInline={
          <button
            type="button"
            onClick={() => onToggle(false)}
            className={moreBtnClass}
          >
            Less
          </button>
        }
      />
    </div>
  ) : (
    <div className={className}>
      <Filter
        value={value}
        onApply={onApply}
        variant="basic"
        compact={compact}
        basicGridClassName={basicGridClassName}
        actionsInline={
          <button
            type="button"
            onClick={() => onToggle(true)}
            className={moreBtnClass}
            aria-label="Expand filters"
          >
            More
          </button>
        }
      />
    </div>
  );
};

export default Filter;
